import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { SEND_MESSAGES } from "../components/ChatAutomations/helpers";
import PageHeader from "../components/PageHeader";
import { ONLINE_NOW_MEMBERS } from "../gql/queries/onlineNow";
import {
  AT_RISK_SEGMENTS,
  FADER_LABELS,
  ONLINE_NOW_TEMPLATES,
  OutreachTemplateGroup,
  SEGMENT_TEMPLATE_GROUP,
  ZONE_COLOURS,
} from "../helpers/const";
import { RECENT_MESSAGES } from "../helpers/recentMessages";
import { Spacing } from "../types/enum";
import { type FaderLevel, type ZoneLabel } from "../types/graphql";

interface OnlineMember {
  userId: string;
  name: string;
  email: string;
  zone: ZoneLabel;
  faderLevel?: FaderLevel | null;
}

const OnlineNowPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [segments, setSegments] = useState<FaderLevel[]>(AT_RISK_SEGMENTS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // One editable message per template group, pre-filled with the ported wording.
  const [templates, setTemplates] = useState<Record<OutreachTemplateGroup, string>>({
    [OutreachTemplateGroup.AtRisk]: ONLINE_NOW_TEMPLATES[OutreachTemplateGroup.AtRisk].text,
    [OutreachTemplateGroup.Never]: ONLINE_NOW_TEMPLATES[OutreachTemplateGroup.Never].text,
    [OutreachTemplateGroup.Paused]: ONLINE_NOW_TEMPLATES[OutreachTemplateGroup.Paused].text,
  });

  const [skipDays, setSkipDays] = useState(7);

  const [loadRecent, { data: recentData }] = useLazyQuery(RECENT_MESSAGES, {
    fetchPolicy: "network-only",
  });

  const [loadMembers, { data, loading }] = useLazyQuery(ONLINE_NOW_MEMBERS, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      const loaded: OnlineMember[] = result?.adminOnlineNowMembers ?? [];
      setSelected(new Set(loaded.map((m) => m.email)));
      // Look up when each of these members was last contacted (wide window, so
      // the skip control can work off it without re-fetching).
      if (loaded.length > 0) {
        void loadRecent({ variables: { userIds: loaded.map((m) => m.userId), withinDays: 60 } });
      }
    },
  });
  const [runSend, { loading: sending }] = useMutation(SEND_MESSAGES);

  // Memoised: a fresh [] each render would re-fire the selection effect below
  // on every render, and setSelected(new Set(...)) would then loop.
  const members: OnlineMember[] = React.useMemo(() => data?.adminOnlineNowMembers ?? [], [data]);

  // How many days ago each member was last contacted, if within the look-back.
  const daysAgoByUser = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const row of recentData?.adminRecentOutreach ?? []) {
      const days = Math.floor((Date.now() - new Date(row.lastAt).getTime()) / 86_400_000);
      map.set(row.userId, days);
    }
    return map;
  }, [recentData]);

  const contactedWithinSkip = React.useCallback(
    (userId: string): boolean => {
      const days = daysAgoByUser.get(userId);
      return days !== undefined && days <= skipDays;
    },
    [daysAgoByUser, skipDays],
  );

  // When the list, the history, or the skip window changes, drop anyone
  // contacted too recently from the selection.
  React.useEffect(() => {
    setSelected(new Set(members.filter((m) => !contactedWithinSkip(m.userId)).map((m) => m.email)));
  }, [members, contactedWithinSkip]);

  const toggleSegment = (segment: FaderLevel) => {
    setSegments((current) =>
      current.includes(segment) ? current.filter((s) => s !== segment) : [...current, segment],
    );
  };

  const onRefresh = () => {
    if (segments.length === 0) {
      enqueueSnackbar("Pick at least one segment.", { variant: "info" });
      return;
    }
    void loadMembers({ variables: { faderLevels: segments } });
  };

  const toggleMember = (email: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  // Selected members grouped by which template they'll get, so at-risk members
  // receive the at-risk message and paused members the paused one.
  const selectedByGroup = React.useMemo(() => {
    const groups = new Map<OutreachTemplateGroup, string[]>();
    for (const member of members) {
      if (!selected.has(member.email) || !member.faderLevel) {
        continue;
      }
      const group = SEGMENT_TEMPLATE_GROUP.get(member.faderLevel);
      if (!group) {
        continue;
      }
      groups.set(group, [...(groups.get(group) ?? []), member.email]);
    }
    return groups;
  }, [members, selected]);

  const onSend = async () => {
    if (selectedByGroup.size === 0) {
      enqueueSnackbar("Select at least one member.", { variant: "info" });
      return;
    }
    const empty = [...selectedByGroup.keys()].find((group) => !templates[group].trim());
    if (empty) {
      enqueueSnackbar(`Write the ${ONLINE_NOW_TEMPLATES[empty].label} message first.`, {
        variant: "info",
      });
      return;
    }

    // Members contacted recently start unticked; if she has put any back, say so
    // once and then let the send through with the server guard turned off for it.
    const recentlySelected = members.filter(
      (member) => selected.has(member.email) && contactedWithinSkip(member.userId),
    );
    let overrideGuard = false;
    if (recentlySelected.length > 0) {
      if (
        !window.confirm(
          `${recentlySelected.length} selected member(s) were sent a chat message in the last ` +
            `${skipDays} days. Send anyway?`,
        )
      ) {
        return;
      }
      overrideGuard = true;
    }

    try {
      let succeeded = 0;
      let failed = 0;
      let skipped = 0;
      // One send per group — each with its own message, all through the
      // throttled backend.
      for (const [group, emails] of selectedByGroup) {
        const { data: result } = await runSend({
          variables: {
            input: {
              emails,
              message: templates[group],
              source: "ONLINE_NOW",
              // The group is the whole point of this screen — at-risk and paused
              // members get different messages, so their results are different
              // questions and the tag keeps them apart.
              campaign: `online_now:${group}`,
              // Rechecked server-side against the log just before sending.
              skipContactedWithinDays: overrideGuard ? null : skipDays,
            },
          },
        });
        const r = result?.chatAutomationsSendMessages;
        succeeded += r?.succeeded ?? 0;
        failed += r?.failed ?? 0;
        skipped += r?.skipped?.length ?? 0;
      }
      enqueueSnackbar(
        `Sent to ${succeeded} member${succeeded === 1 ? "" : "s"}.` +
          (failed ? ` ${failed} failed.` : "") +
          (skipped ? ` ${skipped} skipped — messaged too recently.` : ""),
        { variant: failed || skipped ? "warning" : "success" },
      );
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <PageHeader title="Online Now">
        Members who are on the platform right now and at risk of drifting — so you can greet them
        while they're actually here. Pick the segments, load the list, then message the ones you
        choose.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Stack spacing={Spacing.m}>
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Segments
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {AT_RISK_SEGMENTS.map((segment) => (
                  <FormControlLabel
                    key={segment}
                    control={
                      <Checkbox
                        checked={segments.includes(segment)}
                        onChange={() => toggleSegment(segment)}
                      />
                    }
                    label={FADER_LABELS.get(segment) ?? segment}
                  />
                ))}
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
              <Button variant="outlined" onClick={onRefresh} disabled={loading}>
                {loading ? "Loading…" : "Load online members"}
              </Button>
              <TextField
                label="Skip if contacted within"
                type="number"
                size="small"
                value={skipDays}
                onChange={(event) => setSkipDays(Math.max(0, Number(event.target.value) || 0))}
                slotProps={{ htmlInput: { min: 0, style: { width: 60 } } }}
                helperText="days"
              />
            </Box>

            {data ? (
              members.length === 0 ? (
                <Alert severity="info">No one in these segments is online right now.</Alert>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary">
                    {selected.size} of {members.length} selected
                  </Typography>
                  <TableContainer sx={{ maxHeight: "min(460px, 50vh)" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Name</TableCell>
                          <TableCell>Risk</TableCell>
                          <TableCell>Zone</TableCell>
                          <TableCell>Last contacted</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {members.map((member) => {
                          const daysAgo = daysAgoByUser.get(member.userId);
                          const recent = contactedWithinSkip(member.userId);
                          return (
                            <TableRow
                              key={member.userId}
                              hover
                              sx={recent ? { opacity: 0.55 } : undefined}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selected.has(member.email)}
                                  onChange={() => toggleMember(member.email)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{member.name}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {member.email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {member.faderLevel ? FADER_LABELS.get(member.faderLevel) : "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={member.zone}
                                  sx={{
                                    backgroundColor: ZONE_COLOURS.get(member.zone),
                                    color: "#fff",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {daysAgo === undefined ? (
                                  <Typography variant="caption" color="textSecondary">
                                    —
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color={recent ? "error" : "textSecondary"}
                                    fontWeight={recent ? 600 : 400}
                                  >
                                    {daysAgo === 0 ? "today" : `${daysAgo}d ago`}
                                    {recent ? " · skipped" : ""}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Messages — each member gets the one for her segment. Edit as you like; every
                      message opens with "Hi @Name!" automatically.
                    </Typography>
                    <Stack spacing={Spacing.m} mt={Spacing.s}>
                      {[...selectedByGroup.entries()].map(([group, emails]) => (
                        <TextField
                          key={group}
                          label={`${ONLINE_NOW_TEMPLATES[group].label} — ${emails.length} member(s)`}
                          value={templates[group]}
                          onChange={(event) =>
                            setTemplates((current) => ({ ...current, [group]: event.target.value }))
                          }
                          multiline
                          minRows={3}
                          fullWidth
                        />
                      ))}
                      {selectedByGroup.size === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Select members above to write their messages.
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  <Box>
                    <Button variant="contained" onClick={onSend} disabled={sending}>
                      {sending ? "Sending…" : `Message ${selected.size} member(s)`}
                    </Button>
                  </Box>
                </>
              )
            ) : null}
          </Stack>
        </Box>
      </Card>
    </>
  );
};

export default OnlineNowPage;
